import { Injectable } from '@angular/core';

import { Albergo, Lingua, Nazione, UbicazioneIstat } from '../models/bo.model';
import { ClienteMySelf, LogoutUtenteRQ, MyForecast, RouteInfo, Sessione } from '../models/dto';
import {
  LettereMaiuscole,
  Ruolo,
  SezioneDashboard,
  TipoPermesso,
  TipoPubblicazioneDisponibilitaWelcomeWS,
} from '../models/enum';
import { LocalStorageService } from './local-storage.service';
import { UtilityService } from './utility.service';

@Injectable({
  providedIn: 'root'
})
export class SessionService {

  public readonly SESSION_KEY: string = 'sessione';

  public sessione: Sessione;

  public isAttachRequestHeaders: boolean = false;

  constructor(private localStorage: LocalStorageService, private utility: UtilityService) {
    this.sessione = this.localStorage.get(this.SESSION_KEY);
  }

  isLoggedIn(): boolean {
    return this.sessione != null && ((!this.sessione.isMySelf && this.sessione.utente != null) || (this.sessione.isMySelf && this.sessione.clienteMySelf != null));
  }

  isLoggedInSbm(): boolean {
    return this.sessione != null && !this.sessione.isMySelf && this.sessione.utente != null;
  }

  isLoggedInMySelf(): boolean {
    return this.sessione != null && this.sessione.isMySelf && this.sessione.clienteMySelf != null;
  }

  isMySelf(): boolean {
    return this.sessione != null && this.sessione.isMySelf;
  }

  getOidUtente(): number {
    if (this.sessione != null && this.sessione.utente != null) {
      return this.sessione.utente.oid;
    }

    return 0;
  }

  getClientId(): string {
    if (this.sessione != null) {
      return this.sessione.clientId;
    }

    return null;
  }

  getUbicazioneIstat(): UbicazioneIstat {
    if (this.sessione != null && this.sessione.utente != null && this.sessione.utente.ubicazioneIstat != null && this.sessione.utente.ubicazioneIstat.oid > 0) {
      return this.sessione.utente.ubicazioneIstat;
    }

    return null;
  }

  getCodicePartner(): string {
    if (this.sessione != null && this.sessione.utente != null) {
      return this.sessione.utente.codicePartner;
    }

    return null;
  }

  getUrlSmartCloud(): string {
    if (this.sessione != null && this.sessione.utente != null) {
      return this.sessione.utente.urlSmartCloud;
    }

    return null;
  }

  getOidAlbergo(): number {
    if (this.sessione != null && this.sessione.albergo != null) {
      return this.sessione.albergo.oid;
    }

    return 0;
  }

  getCodiceAlbergo(): number {
    if (this.sessione != null && this.sessione.albergo != null) {
      return this.sessione.albergo.codice;
    }

    return 0;
  }

  getApiKeyAlbergo(): string {
    if (this.sessione != null && this.sessione.albergo != null && this.sessione.albergo.apiKey != null) {
      return this.sessione.albergo.apiKey;
    }

    return '';
  }

  getApiKeyUtente(): string {
    if (this.sessione != null && this.sessione.utente != null && this.sessione.utente.apiKey != null) {
      return this.sessione.utente.apiKey;
    }

    return '';
  }

  getApiKeyCustomer(): string {
    if (this.sessione != null && this.sessione.clienteMySelf != null && this.sessione.clienteMySelf.apiKey != null) {
      return this.sessione.clienteMySelf.apiKey;
    }

    return '';
  }

  getLingua(): string {
    if (this.sessione && this.sessione.clienteMySelf && !!this.sessione.clienteMySelf.lingua) {
      return this.sessione.clienteMySelf.lingua;
    }

    return 'it';
  }

  getNomeAlbergo(): string {
    if (this.sessione && this.sessione.albergo) {
      return this.sessione.albergo.nome;
    }

    return '';
  }

  getAlbergo(): Albergo {
    if (this.sessione) {
      return this.sessione.albergo;
    }

    return null;
  }

  isAlbergoDemo(): boolean {
    if (this.sessione && this.sessione.albergo && this.sessione.albergo.dati) {
      return this.sessione.albergo.dati.isDemo;
    }

    return false;
  }

  is3DSecureAttivo(): boolean {
    if (this.sessione && this.sessione.albergo && this.sessione.albergo.dati) {
      return this.sessione.albergo.dati.is3DSecureAttivo;
    }

    return false;
  }

  getCliente(): ClienteMySelf {
    if (this.sessione) {
      return this.sessione.clienteMySelf;
    }

    return null;
  }

  getRuoloUtente(): Ruolo {
    if (this.sessione && this.sessione.utente) {
      return this.sessione.utente.ruolo;
    }

    return Ruolo.Administrator;
  }

  isAdmin(): boolean {
    let ruolo = this.getRuoloUtente();
    return ruolo == Ruolo.Administrator || ruolo == Ruolo.SuperAdmin;
  }

  getUtente(): string {
    if (this.sessione && this.sessione.utente) {
      return this.sessione.utente.descrizione;
    }

    return null;
  }

  getPermessiUtente(): string[] {
    if (this.sessione && this.sessione.utente && this.sessione.utente.permessi && this.sessione.utente.permessi.length > 0) {
      return this.sessione.utente.permessi;
    }

    return null;
  }

  getPlugins(): RouteInfo[] {
    if (this.sessione && this.sessione.utente && this.sessione.utente.plugins && this.sessione.utente.plugins.length > 0) {
      return this.sessione.utente.plugins;
    }

    return null;
  }

  getOidAnagraficaHotel(): number {
    if (this.sessione && this.sessione.clienteMySelf) {
      return this.sessione.clienteMySelf.oidAnagraficaHotel;
    }

    return 0;
  }

  getMyForecast(): MyForecast[] {
    if (this.sessione) {
      return this.sessione.myForecast;
    }

    return null;
  }

  isCrmAttivo(): boolean {
    if (this.sessione && this.sessione.albergo && this.sessione.albergo.dati) {
      return this.sessione.albergo.dati.isCrmAttivo;
    }

    return false;
  }

  isDmsAttivo(): boolean {
    if (this.sessione && this.sessione.albergo && this.sessione.albergo.dati) {
      return this.sessione.albergo.dati.isDmsAttivo;
    }

    return false;
  }

  isMySelfAttivo(): boolean {
    if (this.sessione && this.sessione.albergo && this.sessione.albergo.dati) {
      return this.sessione.albergo.dati.isMySelfAttivo;
    }

    return false;
  }

  isCrsAttivo(): boolean {
    if (this.sessione && this.sessione.albergo && this.sessione.albergo.dati) {
      return this.sessione.albergo.dati.isCrsAttivo;
    }

    return false;
  }

  isDev(): boolean {
    if (this.sessione && this.sessione.albergo && this.sessione.albergo.dati) {
      return this.sessione.albergo.dati.isDev;
    }

    return false;
  }

  isPro(): boolean {
    if (this.sessione && this.sessione.albergo && this.sessione.albergo.dati) {
      return this.sessione.albergo.dati.isPro;
    }

    return false;
  }

  isPubblicaWS(): boolean {
    if (this.sessione && this.sessione.albergo) {
      return this.sessione.albergo.tipoPubblicazioneDisponibilitaWelcomeWS == TipoPubblicazioneDisponibilitaWelcomeWS.LambdaAWS;
    }

    return false;
  }

  getLingue(): Lingua[] {
    if (this.sessione) {
      return this.sessione.lingue;
    }

    return null;
  }

  isLinguaAttiva(lingua: Lingua): boolean {
    let lingue = this.getLingue();

    if (lingue != null && lingue.length > 0) {
      return lingue.find(x => x.oid == lingua.oid) != null;
    }

    return true;
  }

  getTipoPubblicazioneAttivitaAutomatica(): string {
    if (this.sessione && this.sessione.albergo && this.sessione.albergo.dati) {
      let ret = this.sessione.albergo.dati.tipoPubblicazioneAttivitaAutomatica;

      //2023C1 - aggiunto anche il periodo
      if (this.sessione.albergo.dati.isPubblicazioneDisponibiliHotelAttiva) {
        if (!!this.sessione.albergo.dati.dataInizioPubblicazione || !!this.sessione.albergo.dati.dataFinePubblicazione) {
          ret += " - Sincronizzazione impostata";

          if (!!this.sessione.albergo.dati.dataInizioPubblicazione) {
            ret += " dal " + this.utility.getDate(this.sessione.albergo.dati.dataInizioPubblicazione, 'DD/MM/YYYY');
          }

          if (!!this.sessione.albergo.dati.dataFinePubblicazione) {
            ret += " fino al " + this.utility.getDate(this.sessione.albergo.dati.dataFinePubblicazione, 'DD/MM/YYYY');
          }
        }
      }

      return ret;
    }

    return null;
  }

  isPubblicazioneDisponibiliHotelAttiva(): boolean {
    if (this.sessione && this.sessione.albergo && this.sessione.albergo.dati) {
      return this.sessione.albergo.dati.isPubblicazioneDisponibiliHotelAttiva;
    }

    return false;
  }

  getLettereMaiuscoleAnagrafica(): LettereMaiuscole {
    if (this.sessione && this.sessione.albergo && this.sessione.albergo.lettereMaiuscoleAnagrafica != null) {
      return this.sessione.albergo.lettereMaiuscoleAnagrafica;
    }

    return LettereMaiuscole.Nessuna;
  }

  getUrlApiWelcome(): string {
    if (this.sessione && this.sessione.albergo) {
      return this.sessione.albergo.urlApiWelcome;
    }

    return null;
  }

  getAziendaHotel(): string {
    if (this.sessione && this.sessione.albergo) {
      return this.sessione.albergo.aziendaHotel;
    }

    return null;
  }

  isModificaDisponibiliEnabled(): boolean {
    if (this.isPubblicazioneDisponibiliHotelAttiva()) {
      return false;
    }

    let permessi = this.getPermessiUtente();

    return (permessi == null || !permessi.includes(this.utility.getEnumKeyOriginal(TipoPermesso, TipoPermesso.ModificaDisponibiliPlanning)));
  }

  isModificaVariazioneDisponibiliEnabled(): boolean {
    /*if (this.isPubblicazioneDisponibiliHotelAttiva()) {
      return false;
    }*/

    let permessi = this.getPermessiUtente();

    return (permessi == null || !permessi.includes(this.utility.getEnumKeyOriginal(TipoPermesso, TipoPermesso.ModificaVariazioneDisponibiliPlanning)));
  }

  isPostaAttiva(): boolean {
    return this.sessione?.isPostaAttiva;
  }

  isVisualizzaSoloCamereDisponibili(): boolean {
    if (this.sessione && this.sessione.utente && this.sessione.utente.isVisualizzaSoloCamereDisponibili != null) {
      return this.sessione.utente.isVisualizzaSoloCamereDisponibili;
    }

    return true;
  }

  isVisualizzaSoloTariffePrenotabili(): boolean {
    if (this.sessione && this.sessione.utente && this.sessione.utente.isVisualizzaSoloTariffePrenotabili != null) {
      return this.sessione.utente.isVisualizzaSoloTariffePrenotabili;
    }

    return true;
  }

  isVisualizzaCamereConPaxNonValide(): boolean {
    if (this.sessione && this.sessione.utente && this.sessione.utente.isVisualizzaCamereConPaxNonValide != null) {
      return this.sessione.utente.isVisualizzaCamereConPaxNonValide;
    }

    return false;
  }

  getNazioneTradotta(lingua: string): Nazione {
    if (this.sessione?.clienteMySelf?.nazione != null) {
      let nazione = Object.assign({}, this.sessione.clienteMySelf.nazione);

      if (this.sessione.clienteMySelf.nazioniXLingua != null) {
        let traduzione = this.sessione.clienteMySelf.nazioniXLingua.find(x => x.lingua != null && x.lingua.codice == lingua)?.nome;

        if (!!traduzione) {
          nazione.nome = traduzione;
        } else {
          //nazione.nome = nazione.nomeItaliano;
        }
      }

      return nazione;
    }

    return new Nazione();
  }

  isSezioneDashboardAbilitata(sezioneDashboard: SezioneDashboard) {
    if (!!this.sessione?.utente?.sezioniDashboardAbilitate) {
      return this.sessione.utente.sezioniDashboardAbilitate.split(';').filter(x => !!x && !isNaN(+x)).map(x => +x).includes(sezioneDashboard);
    }

    return true;
  }

  logoutUtente() {
    let oidUtente = this.getOidUtente();
    let clientId = this.getClientId();

    if (oidUtente > 0 && !!clientId) {
      let rq = new LogoutUtenteRQ();
      rq.oidUtente = oidUtente;
      rq.clientId = clientId;
      this.utility.sendKeepAliveRequest('Utenti/Logout', rq);
    }
  }

  clearSessione() {
    this.logoutUtente();
    this.sessione = null;
    this.localStorage.remove(this.SESSION_KEY);
  }
}